import {
  MagicTableCell,
  MagicTable,
  useMagicTableInfiniteQuery,
  useLinkMagicTableWithSearchParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  MagicTableCellEmpty,
  MagicTableFilterConfig,
  SequenceMenu,
  SetIsOpen,
  Button,
  Badge,
  Icon3dBox,
  IconPeopleIdCard,
  IconPlus,
  IconScale
} from '@sequencehq/core-components'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import Page from 'components/Page'
import { StickyPanelFrame } from 'components/Page/StickyPanelFrame'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  BillingProduct,
  Product,
  ProductsFilters,
  ProductsView
} from 'modules/Products/types'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { uniq } from 'lodash'
import { ProductsGraphs } from 'modules/Products/ProductGraphs/ProductsGraphs'
import { isDemoEnv } from 'lib/environment/environment'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  RevenueRecognitionMethod,
  revenueRecognitionOptions
} from 'common/types/revenueRecognition.types'
import { toRevenueRecognitionMethodLabel } from 'modules/RevenueRecognition'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { TableLinksLogos } from 'modules/Integrations'
import { ArchiveProductMenuItem } from './menuItems'
import { taxCategoryIdFilterConfig } from './productMagicTableFilter'

export const Products = () => {
  const flags = useFeatureFlags()
  const navigate = useNavigate()

  const tableFilters: MagicTableFilterConfig<ProductsFilters>[] = [
    {
      type: 'text',
      paramName: 'name',
      label: 'Name',
      icon: IconPeopleIdCard
    },
    {
      ...taxCategoryIdFilterConfig,
      paramName: 'taxCategoryId'
    },
    ...(flags.useRevRec
      ? [
          {
            type: 'multiSelect' as const,
            paramName: 'recognitionMethod' as const,
            label: 'Revenue recognition method',
            searchPlaceholder: 'Search method',
            icon: IconScale,
            options: revenueRecognitionOptions,
            format: (value: string) =>
              toRevenueRecognitionMethodLabel(value as RevenueRecognitionMethod)
          }
        ]
      : [])
  ]

  const { activeFilters, onChangeActiveFilters } =
    useLinkMagicTableWithSearchParams(tableFilters)

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getBillingProducts,
    tableFilters,
    activeFilters
  )

  const reload = () => {
    void infiniteQuery.refetch()
  }

  const { can } = usePermissions()

  return (
    <StickyPanelFrame>
      <Page
        title="Products"
        addNewConfig={{
          button: (
            <ProtectedButton permission="org.product:create">
              <Button
                variant="primary"
                leadingIcon={<IconPlus color="inherit" />}
                onClick={() => navigate('/products/new')}
                data-testid="TopNavigation.add-new"
              >
                New product
              </Button>
            </ProtectedButton>
          )
        }}
        paddingBottom={0}
      >
        <CurrentUserId>
          {userId => (
            <>
              {isDemoEnv() && <ProductsGraphs />}
              <MagicTable<ProductsView>
                entityNamePlural="products"
                infiniteQuery={infiniteQuery}
                sequenceUserId={userId}
                activeFilters={activeFilters}
                onChangeActiveFilters={onChangeActiveFilters}
                filters={tableFilters}
                rowPath={(row: BillingProduct) => `/products/${row.id}`}
                columns={[
                  {
                    id: 'name',
                    header: 'Product',
                    accessorFn: (row: BillingProduct) => row,
                    cell: row => {
                      const product = row.getValue<BillingProduct>()

                      return (
                        <MagicTableCell
                          data-testid={`products.${product.id}.name`}
                          text={product.name}
                        />
                      )
                    }
                  },
                  {
                    id: 'taxCategory',
                    header: 'Tax category',
                    accessorKey: 'taxCategoryName',
                    cell: (row: { getValue: () => any }) => {
                      const value = row.getValue()
                      if (!value) return null
                      return (
                        <MagicTableCellEmpty>
                          <Badge textTransform="none" truncate>
                            {value}
                          </Badge>
                        </MagicTableCellEmpty>
                      )
                    }
                  },
                  ...(flags.useRevRec
                    ? [
                        {
                          id: 'recognitionMethod',
                          header: 'Revenue recognition method',
                          accessorKey: 'recognitionMethod',
                          cell: (row: { getValue: () => any }) => {
                            const value =
                              row.getValue() as RevenueRecognitionMethod | null

                            if (!value) {
                              return null
                            }

                            const label = toRevenueRecognitionMethodLabel(value)

                            return <MagicTableCell text={label} />
                          }
                        }
                      ]
                    : []),
                  {
                    id: 'priceVariants',
                    header: 'Price Variants',
                    accessorKey: 'prices',
                    cell: row => {
                      const value = row.getValue<BillingProduct['prices']>()
                      return <MagicTableCell text={value.length || '—'} />
                    }
                  },
                  {
                    id: 'integrationIds',
                    header: 'Links',
                    accessorFn: (row: BillingProduct) => row,
                    cell: row => {
                      const product = row.getValue<BillingProduct>()

                      // A product links to a service more than once — NetSuite gets
                      // both a service item and a ledger account — and this list
                      // doesn't say which is which, so show one logo per service.
                      const services = uniq(
                        product.integrationIds.map(
                          integration => integration.service
                        )
                      )

                      if (!services.length) {
                        return null
                      }

                      return (
                        <MagicTableCellEmpty
                          data-testid={`products.${product.id}.links`}
                        >
                          <TableLinksLogos services={services} />
                        </MagicTableCellEmpty>
                      )
                    }
                  },
                  {
                    id: 'createdAt',
                    header: 'Created',
                    accessorKey: 'createdAt',
                    cell: row => (
                      <div data-testid="price-list-created-at-row">
                        <MagicTableCell
                          text={dateTimeWithFormat(
                            row.getValue<BillingProduct['createdAt']>(),
                            'd MMM, yyyy'
                          )}
                        />
                      </div>
                    )
                  },
                  {
                    id: 'updatedAt',
                    header: 'Updated',
                    accessorKey: 'updatedAt',
                    cell: row => (
                      <div data-testid="price-list-updated-at-row">
                        <MagicTableCell
                          text={dateTimeWithFormat(
                            row.getValue<BillingProduct['updatedAt']>(),
                            'd MMM, yyyy'
                          )}
                        />
                      </div>
                    )
                  }
                ]}
                emptyStateUnfiltered={entityNamePlural => (
                  <MagicTableEmptyState
                    entityNamePlural={entityNamePlural}
                    imageSrc="/img/empty/products.png"
                    learnMoreText="Manage products and prices with the product catalog"
                    learnMoreHref="https://docs.sequencehq.com/plans-products/product-catalogue"
                    buttonNew={
                      <ProtectedButton permission="org.product:create">
                        <Button
                          variant="primary"
                          leadingIcon={<IconPlus color="inherit" />}
                          onClick={() => navigate('/products/new')}
                          data-testid="TopNavigation.add-new"
                        >
                          New product
                        </Button>
                      </ProtectedButton>
                    }
                  />
                )}
                emptyStateFiltered={(entityNamePlural, clearFilters) => (
                  <MagicTableFilterEmptyState
                    entityNamePlural={entityNamePlural}
                    entityIcon={Icon3dBox}
                    actionClearFilters={clearFilters}
                    actionViewAll={() => navigate('/products')}
                    variant="TABLE"
                  />
                )}
                kebabMenu={(product: Product, props) => {
                  return can('org.product:archive') ? (
                    <SequenceMenu
                      data-testid={`products.${product.id}.menu`}
                      buttonSize="small"
                      alignment="bottom-right"
                      items={[
                        ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                          <ArchiveProductMenuItem
                            key="archive-product"
                            productId={product.id}
                            productName={product.name}
                            onClose={() => {
                              setIsOpen?.(false)
                            }}
                            onSuccess={() => {
                              setIsOpen?.(false)
                              reload()
                            }}
                          />
                        )
                      ]}
                      {...props}
                    />
                  ) : undefined
                }}
              />
            </>
          )}
        </CurrentUserId>
        <Outlet />
      </Page>
    </StickyPanelFrame>
  )
}
